import { useEffect, useMemo, useState, useCallback } from "react";
import { Briefcase, Wallet, Receipt, ArrowDownCircle, ArrowUpCircle, ArrowUpRight, ArrowLeft, FolderKanban } from "lucide-react";
import CrmPanel from "./CrmPanel";
import SummaryCards from "./SummaryCards";
import StatusPill from "./StatusPill";
import { crmList, crmCreate, crmUpdate, crmDelete, crmProjectsSummary, crmBackfillWebsiteStatus, adminListClients } from "@/lib/api";
import { INR, fmtDate, PROJECT_STATUSES, PROJECT_GROUPS, PROJECT_STATUS_TONE } from "./crmUtils";
import ProjectDetail from "./ProjectDetail";

// Client-facing status options (mirrors the user's project status sheet)
const CLIENT_STATUS_OPTIONS = [
  { value: "",                              label: "— Not shown —" },
  { value: "Planning",                      label: "Planning" },
  { value: "Requirement Gathering",         label: "Requirement Gathering" },
  { value: "Design In Progress",            label: "Design In Progress" },
  { value: "Development In Progress",       label: "Development In Progress" },
  { value: "Testing In Progress",           label: "Testing In Progress" },
  { value: "Client Review (V1 Delivered)",  label: "Client Review (V1 Delivered)" },
  { value: "Revisions In Progress",         label: "Revisions In Progress" },
  { value: "Live",                          label: "Live" },
  { value: "Support & Maintenance",         label: "Support & Maintenance" },
  { value: "Completed",                     label: "Completed" },
  { value: "On Hold",                       label: "On Hold" },
  { value: "Cancelled",                     label: "Cancelled" },
];
const WEBSITE_STATUS_OPTIONS = [
  { value: "",             label: "— Not shown —" },
  { value: "Planning",     label: "Planning" },
  { value: "Development",  label: "Development" },
  { value: "Testing",      label: "Testing" },
  { value: "Live",         label: "Live" },
  { value: "Maintenance",  label: "Maintenance" },
  { value: "Hold",         label: "Hold" },
  { value: "Completed",    label: "Completed" },
  { value: "Cancelled",    label: "Cancelled" },
];

const HOSTING_SERVER_OPTIONS = [
  { value: "",             label: "— None —" },
  { value: "Vercel",       label: "Vercel" },
  { value: "Netlify",      label: "Netlify" },
  { value: "Shopify",      label: "Shopify" },
  { value: "Reachvel VPS", label: "Reachvel VPS" },
  { value: "AWS",          label: "AWS" },
  { value: "Cloudflare",   label: "Cloudflare" },
  { value: "Others",       label: "Others" },
];

// Tone map for the Website Status pill (new 8-option set + legacy fallbacks)
export const WEBSITE_STATUS_TONE = {
  "Planning":            "bg-cyan-100 text-cyan-800 border-cyan-300",
  "Development":         "bg-sky-100 text-sky-800 border-sky-300",
  "Testing":             "bg-violet-100 text-violet-800 border-violet-300",
  "Live":                "bg-green-100 text-green-800 border-green-400",
  "Maintenance":         "bg-amber-100 text-amber-800 border-amber-400",
  "Maintainence":        "bg-amber-100 text-amber-800 border-amber-400",
  "Hold":                "bg-yellow-100 text-yellow-800 border-yellow-400",
  "Completed":           "bg-emerald-100 text-emerald-800 border-emerald-400",
  "Cancelled":           "bg-rose-100 text-rose-800 border-rose-300",
  // Legacy values (kept until migration runs)
  "Development Server":  "bg-sky-100 text-sky-800 border-sky-300",
  "Testing Server":      "bg-violet-100 text-violet-800 border-violet-300",
  "Closed":              "bg-zinc-200 text-zinc-700 border-zinc-400",
};

// Tone map for client-facing Project Status pills — mirrors ClientPortal palette
const CLIENT_STATUS_TONE = {
  "Planning":                       "bg-cyan-100 text-cyan-800 border-cyan-300",
  "Requirement Gathering":          "bg-blue-100 text-blue-800 border-blue-300",
  "Design In Progress":             "bg-fuchsia-100 text-fuchsia-800 border-fuchsia-300",
  "Development In Progress":        "bg-indigo-100 text-indigo-800 border-indigo-300",
  "Testing In Progress":            "bg-violet-100 text-violet-800 border-violet-300",
  "Client Review (V1 Delivered)":   "bg-amber-100 text-amber-800 border-amber-300",
  "Revisions In Progress":          "bg-orange-100 text-orange-800 border-orange-300",
  "Live":                           "bg-green-100 text-green-800 border-green-400",
  "Support & Maintenance":          "bg-teal-100 text-teal-800 border-teal-300",
  "Completed":                      "bg-emerald-100 text-emerald-800 border-emerald-400",
  "On Hold":                        "bg-yellow-100 text-yellow-800 border-yellow-400",
  "Cancelled":                      "bg-rose-100 text-rose-800 border-rose-300",
};

export default function CrmProjects({ token }) {
  const [selectedId, setSelectedId] = useState(null);
  const [leads, setLeads] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [clients, setClients] = useState([]);
  const [summary, setSummary] = useState(null);          // financial summary — kept for internal reference (not shown as cards)
  const [allProjects, setAllProjects] = useState([]);    // Rows for the current view (filter-aware) — powers the KPI cards

  const loadSummary = useCallback(async () => {
    try { setSummary(await crmProjectsSummary(token)); } catch { /* noop */ }
    try { setAllProjects(await crmList(token, "projects", {})); } catch { /* noop */ }
  }, [token]);

  useEffect(() => {
    crmList(token, "leads").then(setLeads).catch(() => {});
    crmList(token, "vendors").then(setVendors).catch(() => {});
    adminListClients(token).then(setClients).catch(() => {});
    loadSummary();
  }, [token, loadSummary]);

  const fields = useMemo(() => [
    { name: "name",          label: "Project name", type: "text", required: true, full: true, placeholder: "Vault Private Banking Platform" },
    { name: "client",        label: "Client",       type: "text", placeholder: "Vault Holdings" },
    { name: "client_id",     label: "Client login", type: "select",
      options: [{ value: "", label: "— None —" }, ...clients.map((c) => ({ value: c.id, label: `${c.name}${c.username ? ` · ${c.username}` : ""}` }))],
      help: "Linking gives this client portal access to the project." },
    { name: "contact_person", label: "Primary contact person", type: "text", placeholder: "Jane Doe" },
    { name: "contact_phone",  label: "Mobile number",          type: "text", placeholder: "+91 98765 43210" },
    { name: "contact_email",  label: "Email ID",               type: "text", placeholder: "jane@vault.com" },
    { name: "project_group", label: "Project group", type: "select",
      options: [{ value: "", label: "— Select —" }, ...PROJECT_GROUPS.map((g) => ({ value: g.k, label: g.label }))] },
    { name: "project_type",  label: "Project type (client-facing)", type: "text", placeholder: "Website · CRM Application · Mobile App",
      help: "Shown on the client portal. Falls back to Project Group if empty." },
    { name: "worktype", label: "Worktype", type: "select",
      options: [{ value: "", label: "— Select —" }, { value: "Build", label: "Build" }, { value: "Enhance", label: "Enhance" }],
      help: "Build = new build from scratch. Enhance = ongoing improvements to an existing product." },
    { name: "gst_applicable", label: "GST", type: "select",
      options: [{ value: "true", label: "GST" }, { value: "false", label: "Non-GST" }],
      help: "Whether GST applies to this project's invoices/expenses." },
    { name: "lead_id",       label: "Linked lead",   type: "select",
      options: [{ value: "", label: "— None —" }, ...leads.map((l) => ({ value: l.id, label: `${l.name}${l.company ? ` · ${l.company}` : ""}` }))] },
    { name: "vendor_id",     label: "Primary vendor", type: "select",
      options: [{ value: "", label: "— None —" }, ...vendors.map((v) => ({ value: v.id, label: v.name || v.company || v.id }))] },
    { name: "status",        label: "Status (internal)", type: "select", options: PROJECT_STATUSES.map((s) => ({ value: s.k, label: s.label })) },
    { name: "client_status", label: "Project status (internal only)", type: "select", options: CLIENT_STATUS_OPTIONS,
      help: "Internal-only. Not visible in the client portal." },
    { name: "website_status", label: "Website status (client-facing)", type: "select", options: WEBSITE_STATUS_OPTIONS,
      help: "This is the status your client sees on the Client Portal." },
    { name: "hosting_billable", label: "Hosting billable?", type: "select",
      options: [{ value: "false", label: "No" }, { value: "true", label: "Yes" }],
      help: "If yes, hosting will be billed separately (AMC / renewal)." },
    { name: "hosting_server", label: "Hosting server", type: "select",
      options: HOSTING_SERVER_OPTIONS },
    { name: "deployed_live_on", label: "Deployed to LIVE on", type: "date",
      help: "Used for maintenance renewal date (deployed + 1 year)." },
    { name: "amc_amount", label: "AMC amount (₹)", type: "money",
      help: "Annual maintenance charge — covers hosting, security & minor changes." },
    { name: "live_url",      label: "Live URL", type: "text", placeholder: "https://www.example.com" },
    { name: "revisions_url", label: "Revisions URL", type: "text", placeholder: "https://staging.example.com" },
    { name: "eta",           label: "ETA", type: "date" },
    { name: "latest_update", label: "Latest update", type: "textarea", full: true, rows: 2, help: "One-line status update shown to the client." },
    { name: "start_date",    label: "Start date",   type: "date" },
    { name: "end_date",      label: "End date",     type: "date" },
    { name: "budget",        label: "Project Budget (₹)", type: "money" },
    { name: "description",   label: "Description (internal)",  type: "textarea", full: true, rows: 3 },
  ], [leads, vendors, clients]);

  // Coerce string→bool for boolean-ish select fields when saving
  const wrap = (fn) => async (payload) => {
    if (typeof payload.gst_applicable === "string") {
      payload.gst_applicable = payload.gst_applicable === "true";
    }
    if (typeof payload.hosting_billable === "string") {
      payload.hosting_billable = payload.hosting_billable === "true";
    }
    return fn(payload);
  };

  const columns = [
    {
      key: "name", label: "Project",
      render: (r) => {
        const seenKey = `reachvel_admin_seen_${r.id}`;
        const lastSeen = (typeof window !== "undefined" && localStorage.getItem(seenKey)) || "";
        const hasUnread = r.last_comment_author_type === "client"
          && r.last_comment_at
          && r.last_comment_at > lastSeen;
        return (
          <button
            onClick={() => setSelectedId(r.id)}
            data-testid={`crm-open-project-${r.id}`}
            className="text-left"
          >
            <div className="flex items-center gap-2">
              <span className="font-semibold text-[#0a0a0a] hover:text-[#ff5722] transition-colors">{r.name}</span>
              {hasUnread && (
                <span data-testid={`crm-unread-${r.id}`} className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[9px] font-mono font-bold uppercase tracking-[0.1em] bg-[#ff5722] text-white rounded-full animate-pulse">
                  <span className="h-1.5 w-1.5 rounded-full bg-white" /> New comment
                </span>
              )}
            </div>
            {r.description && <div className="text-xs text-[#4a4a4a] truncate max-w-[24ch]">{r.description}</div>}
          </button>
        );
      },
    },
    { key: "project_group", label: "Group",  render: (r) => r.project_group ? <span className="px-2 py-0.5 text-[10px] font-mono uppercase tracking-[0.1em] bg-[#ff5722]/10 text-[#ff5722] rounded">{r.project_group}</span> : "—" },
    { key: "worktype", label: "Worktype",
      render: (r) => r.worktype
        ? <span className={`inline-block whitespace-nowrap px-2 py-0.5 text-[10px] font-mono uppercase tracking-[0.1em] rounded ${r.worktype === "Build" ? "bg-blue-100 text-blue-800" : "bg-fuchsia-100 text-fuchsia-800"}`}>{r.worktype}</span>
        : <span className="text-xs text-[#9a9a9a]">—</span> },
    { key: "website_status", label: "Website Status",
      render: (r) => r.website_status
        ? <span className={`inline-block whitespace-nowrap px-2.5 py-1 text-[10px] font-mono uppercase tracking-[0.1em] border rounded-full ${WEBSITE_STATUS_TONE[r.website_status] || "bg-zinc-100 text-zinc-700 border-zinc-300"}`}>{r.website_status}</span>
        : <span className="text-xs text-[#9a9a9a]">—</span> },
    { key: "end_date",   label: "ETA",    render: (r) => <span className="font-mono text-xs">{fmtDate(r.end_date)}</span> },
    {
      key: "open", label: "", render: (r) => (
        <button
          onClick={() => setSelectedId(r.id)}
          data-testid={`crm-open-detail-${r.id}`}
          className="inline-flex items-center gap-1 text-xs font-bold uppercase tracking-[0.15em] text-[#ff5722] hover:text-[#0a0a0a]"
        >
          Open <ArrowUpRight className="h-3 w-3" />
        </button>
      ),
    },
  ];

  const websiteStatusCounts = useMemo(() => {
    const norm = (s) => {
      if (!s) return "";
      const map = {
        "Development Server": "Development",
        "Testing Server":     "Testing",
        "Closed":             "Cancelled",
        "Maintainence":       "Maintenance",
      };
      return map[s] || s;
    };
    const counts = { Planning: 0, Live: 0, Testing: 0, Development: 0, Maintenance: 0, Hold: 0, Completed: 0, Cancelled: 0 };
    allProjects.forEach((p) => {
      const k = norm(p.website_status);
      if (k in counts) counts[k] += 1;
    });
    return counts;
  }, [allProjects]);

  if (selectedId) {
    return (
      <div>
        <button
          onClick={() => { setSelectedId(null); loadSummary(); }}
          data-testid="crm-back-to-projects"
          className="inline-flex items-center gap-2 text-sm text-[#4a4a4a] hover:text-[#ff5722] mb-4"
        >
          <ArrowLeft className="h-4 w-4" /> All projects
        </button>
        <ProjectDetail token={token} projectId={selectedId} />
      </div>
    );
  }

  const cards = [
    { label: "Total projects", value: allProjects.length,                icon: <FolderKanban className="h-3.5 w-3.5" />, tone: "bg-zinc-500" },
    { label: "Planning",       value: websiteStatusCounts.Planning,      icon: <Briefcase className="h-3.5 w-3.5" />,     tone: "bg-cyan-500" },
    { label: "Development",    value: websiteStatusCounts.Development,   icon: <Briefcase className="h-3.5 w-3.5" />,     tone: "bg-sky-500" },
    { label: "Testing",        value: websiteStatusCounts.Testing,       icon: <Briefcase className="h-3.5 w-3.5" />,     tone: "bg-violet-500" },
    { label: "Live",           value: websiteStatusCounts.Live,          icon: <ArrowUpCircle className="h-3.5 w-3.5" />, tone: "bg-emerald-500", accent: "text-emerald-700" },
    { label: "Maintenance",    value: websiteStatusCounts.Maintenance,   icon: <Wallet className="h-3.5 w-3.5" />,        tone: "bg-amber-500" },
    { label: "Hold",           value: websiteStatusCounts.Hold,          icon: <Briefcase className="h-3.5 w-3.5" />,     tone: "bg-yellow-500" },
    { label: "Completed",      value: websiteStatusCounts.Completed,     icon: <ArrowUpCircle className="h-3.5 w-3.5" />, tone: "bg-emerald-500" },
    { label: "Cancelled",      value: websiteStatusCounts.Cancelled,     icon: <ArrowDownCircle className="h-3.5 w-3.5" />, tone: "bg-rose-500" },
  ];

  return (
    <>
      <SummaryCards cards={cards} />
      <div className="mb-4 flex justify-end">
        <button
          data-testid="crm-backfill-website-status"
          onClick={async () => {
            if (!window.confirm("Migrate legacy website statuses (Development Server → Development, Testing Server → Testing, Closed → Cancelled, Maintainence → Maintenance)?")) return;
            try {
              const res = await crmBackfillWebsiteStatus(token);
              const total = Object.values(res.migrated || {}).reduce((s, v) => s + (v?.updated || 0), 0);
              // Reload the KPI list
              try { setAllProjects(await crmList(token, "projects", {})); } catch { /* noop */ }
              window.dispatchEvent(new Event("crm-projects-refresh"));
              alert(`Migration complete. ${total} project(s) updated.`);
            } catch (e) {
              alert("Migration failed: " + (e?.response?.data?.detail || e.message));
            }
          }}
          className="text-[10px] font-mono uppercase tracking-[0.2em] text-[#4a4a4a] border border-black/15 px-3 py-1.5 rounded-full hover:border-[#ff5722] hover:text-[#ff5722]"
        >
          Migrate legacy statuses
        </button>
      </div>
      <CrmPanel
        title="Reachvel Projects"
        entityName="projects"
        description="Internal delivery projects with tasks, expenses, vendor payments and invoices."
        fields={fields}
        columns={columns}
        list={async (p) => {
          const rows = await crmList(token, "projects", p);
          // KPI cards mirror whatever the user is currently seeing — filter aware.
          setAllProjects(rows);
          return rows;
        }}
        create={wrap((p) => crmCreate(token, "projects", p))}
        update={(id, p) => wrap((pp) => crmUpdate(token, "projects", id, pp))(p)}
        remove={(id) => crmDelete(token, "projects", id)}
        filters={[
          { key: "website_status", label: "Website Status", type: "dropdown",
            options: WEBSITE_STATUS_OPTIONS.filter((o) => o.value).map((o) => ({ k: o.value, label: o.label })) },
          { key: "hosting_server", label: "Hosted in", type: "dropdown",
            options: HOSTING_SERVER_OPTIONS.filter((o) => o.value).map((o) => ({ k: o.value, label: o.label })) },
          { key: "project_group", label: "Group", type: "dropdown", options: PROJECT_GROUPS },
          { key: "worktype", label: "Worktype", type: "dropdown",
            options: [{ k: "Build", label: "Build" }, { k: "Enhance", label: "Enhance" }] },
          { key: "client_id", label: "Client", type: "dropdown",
            options: [...clients]
              .filter((c) => c && c.id)
              .sort((a, b) => (a.name || "").localeCompare(b.name || ""))
              .map((c) => ({ k: c.id, label: c.name })) },
          { key: "gst_applicable", label: "GST", type: "dropdown",
            options: [{ k: "true", label: "GST" }, { k: "false", label: "Non-GST" }] },
        ]}
        initialForm={{ status: "planning", budget: 0, gst_applicable: "true", hosting_billable: "false" }}
        onChange={loadSummary}
      />
    </>
  );
}
